import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  NgZone,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerJourneyService } from '../../service/customer-journey/customer-journey.service';
import { QueryParamsService } from '../../service/query-params/query-params.service';
import {
  AttachmentData,
  AuthorizedPaymentMethod,
  Market,
  PaymentMethod,
  PaymentRequestDto,
  PaymentMethodViewContext
} from '../../model/payment-request/payment-request.dto';
import { CreditService, PreliminaryInstallmentDto } from '@app/service/credit/credit.service';
import {
  FrontendEventType,
  LoggingService,
} from '@app/service/logging-service/logging.service';
import { SelectorItem } from '@app/component/selector/selector.component';
import { CheckoutState, StateService } from '@app/service/state/state.service';
import { ValuePipe } from '@app/shared/pipe/value.pipe';
import { PaymentMethodDetailService } from '@app/service/payment-method-detail/payment-method-details.service';
import { PaymentRequestService } from '@app/service/paymentrequest/payment-request.service';
import { Subscription } from 'rxjs';
import { routeNames } from '@assets/val/route-constants';
import { TermsLinkService } from '@app/service/terms-link/terms-link.service';
import { IframeService, WindowMode } from '@app/service/iframe/iframe.service';
import { LoaderMessagingService } from '@app/service/loader-messaging/loader-messaging.service';
import { ColorService } from '@app/service/color/color.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CreditDecisionDialogComponent } from '@app/component/dialog/credit-decision/credit-decision-dialog.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ThemeProviderService } from '@app/service/theme-provider/theme-provider.service';
import { DarkModeService } from '@app/service/theme-provider/darkmode.service';
import { RouteComponent } from '../route.component';
import { HeaderService } from '@app/service/header/header.service';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-payment-method-selection',
  templateUrl: './payment-method-selection.component.html',
  styleUrls: [
    '../page-shared.css',
    './payment-method-selection.component.scss',
  ],
})
export class PaymentMethodSelectionComponent
  extends RouteComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  loading = true;
  market: Market;
  isMobile: boolean = false;

  availablePaymentMethods: AuthorizedPaymentMethod[];
  paymentMethodContexts: PaymentMethodViewContext[];
  selectedPaymentMethod: AuthorizedPaymentMethod;
  paymentRequest: PaymentRequestDto;
  navigationInProgress: boolean;
  selectorItems: SelectorItem[];
  selectedItem: SelectorItem;

  modalActive: boolean;
  modalLogo: string;
  ctaLabel: string;
  public activeTab: string;
  private tabClosedSubscription: Subscription;
  private windowCheckInterval: any;
  public primaryColor: string;
  public paymentActive: boolean;
  loaderEvent = this.loadermessagingService.handleMessage.bind(
    this.loadermessagingService
  );
  private dialogReference: MatDialogRef<any, any> = null;

  state: CheckoutState;
  Market = Market;
  isIframe: boolean = false;
  childWindow: Window;

  attachmentData: AttachmentData[] = [];
  attachmentDataSubscription: Subscription;

  showPreliminaryInstallments = false;
  preliminaryInstallmentPlans: PreliminaryInstallmentDto[] = [];

  constructor(
    private store: Store,
    private customerJourneyService: CustomerJourneyService,
    protected router: Router,
    private queryParamsService: QueryParamsService,
    private loggingService: LoggingService,
    private valuePipe: ValuePipe,
    private paymentDetail: PaymentMethodDetailService,
    private paymentRequestService: PaymentRequestService,
    public termsLinkService: TermsLinkService,
    private iframeService: IframeService,
    private loadermessagingService: LoaderMessagingService,
    private colorService: ColorService,
    private dialogService: MatDialog,
    private deviceDetectorService: DeviceDetectorService,
    private ngZone: NgZone,
    private themeProviderService: ThemeProviderService,
    private darkModeService: DarkModeService,
    protected stateService: StateService,
    protected route: ActivatedRoute,
    protected headerService: HeaderService,
    private creditService: CreditService
  ) {
    super(router, route, stateService, headerService);
    // Keep the existing event subscription as backup
    this.tabClosedSubscription = this.loadermessagingService.event$.subscribe(
      () => {
        this.ngZone.run(() => {
          this.checkAndCloseDialog();
        });
      }
    );
  }

  async ngOnInit() {
    this.isIframe = this.iframeService.isThisInIframe();
    this.isMobile = this.deviceDetectorService.isMobile();
    this.primaryColor = this.colorService.getPrimaryColor();
    this.paymentRequest = this.store.snapshot().paymentRequest.request;
    this.themeProviderService = this.themeProviderService;

    if (!this.stateService.getToken() && this.queryParamsService.getQueryParams().id) {
      this.stateService.setToken(this.queryParamsService.getQueryParams().id);
    }

    if (this.paymentRequest) {
      localStorage.setItem('zco_market', this.paymentRequest.market);
    }

    if (this.paymentRequest && this.paymentRequest.darkModeSetting) {
      this.darkModeService.setModeFromBackend(this.paymentRequest.darkModeSetting);
    } 

    this.availablePaymentMethods = this.paymentRequest.availableMethods;
    this.market = this.paymentRequest.market;
    this.ctaLabel = this.paymentDetail.setupMarketBasedLabels(this.market);
    this.modalLogo = 'assets/img/Zaver_Logotype_Circle_Coal_DE.svg';

    this.paymentMethodContexts = this.availablePaymentMethods.map(method => 
      this.paymentDetail.getPaymentMethodViewContext(method, this.market, this.paymentRequest)
    );

    if (
      this.availablePaymentMethods == null ||
      this.availablePaymentMethods.length === 0
    ) {
      // If there are no applicable payment methods for this payment request (usually due to misconfiguration of
      // the merchant) we end up here.
      return;
    }

    if (
      this.availablePaymentMethods.length === 1 &&
      this.availablePaymentMethods[0].type === PaymentMethod.CREDIT
    ) {
      const paymentRequestId = this.queryParamsService.getQueryParams().id;
      this.creditService
        .fetchPreliminaryInstallments(paymentRequestId)
        .subscribe((plans) => {
          if (plans && plans.length) {
            this.preliminaryInstallmentPlans = plans.sort(
              (a, b) => a.monthlyCost - b.monthlyCost
            );
            this.showPreliminaryInstallments = true;
          }
        });
    }

    this.setPaymentMethodSelection();
    if (this.paymentRequest.specificPaymentMethodType) {
      await this.navigateIfPreselectedPaymentMethod();
    }


    this.loading = false;
  }

  async navigateIfPreselectedPaymentMethod(): Promise<void> {
    if (this.isBackendDrivenSelection(this.paymentRequest.specificPaymentMethodType)) {
      await this.navigateIfPreselectedBackendDriven();
    } else {
      this.navigateIfPreselectedFrontendDriven();
    }
  }

  async navigateIfPreselectedBackendDriven(): Promise<void> {
    const specificRoute = await this.stateService
      .registerChosenMethod(this.paymentRequest.specificPaymentMethod)
      .then((checkoutState) => {
        return this.stateService.getRouteFromState(checkoutState);
      });
    this.router.navigate([`/${specificRoute}`], {
      queryParamsHandling: 'preserve',
      queryParams: {
        method: this.paymentRequest.specificPaymentMethod,
        methodType: this.paymentRequest.specificPaymentMethodType,
      },
    });
  }

  navigateIfPreselectedFrontendDriven(): void {
    switch (this.paymentRequest.specificPaymentMethodType) {
      case PaymentMethod.TRUSTLY:
        this.router.navigate([`/${routeNames.PERSONAL_NUMBER_ENTRY}`], {
          queryParamsHandling: 'merge',
          queryParams: {
            method: this.paymentRequest.specificPaymentMethod,
            methodType: this.paymentRequest.specificPaymentMethodType,
          },
        });
        break;
      case PaymentMethod.INVOICE:
      case PaymentMethod.CREDIT:
      case PaymentMethod.CREDIT_STATIC_AMOUNT:
        this.router.navigate([`/${routeNames.AUTH}`], {
          queryParamsHandling: 'merge',
          queryParams: {
            method: this.paymentRequest.specificPaymentMethod,
            methodType: this.paymentRequest.specificPaymentMethodType,
          },
        });
        break;
      case PaymentMethod.SWISH_EXTERNAL:
        this.router.navigate([`/${routeNames.SWISH}`], {
          queryParamsHandling: 'merge',
          queryParams: {
            method: this.paymentRequest.specificPaymentMethod,
            methodType: this.paymentRequest.specificPaymentMethodType,
          },
        });
        break;
      case PaymentMethod.INSTANT_DEBIT:
        this.router.navigate([`/${routeNames.TRUSTLY_DIRECT_DEBIT}`], {
          queryParamsHandling: 'merge',
          queryParams: {
            method: this.paymentRequest.specificPaymentMethod,
            methodType: this.paymentRequest.specificPaymentMethodType,
          },
        });
        break;
      default:
        this.loggingService.warn(
          'Unhandled case when trying to navigate to specific payment method flow: ' +
            this.paymentRequest.specificPaymentMethodType.toString()
        );
        break;
    }
  }

  ngAfterViewInit() {
    this.loggingService.logEvent(
      FrontendEventType.DISPLAYED_PAYMENT_OPTIONS,
      'displayed options: ' +
        JSON.stringify(
          this.paymentRequest.availableMethods.map((pm) => pm.type)
        )
    );

    this.attachmentDataSubscription = this.paymentRequestService
      .getAttachmentData()
      .subscribe((resp) => {
        if (resp && resp.length > 0) {
          this.attachmentData = resp;
        } else {
          this.paymentRequestService
            .fetchAttachmentsByPaymentRequest(this.paymentRequest)
            .subscribe((resp) => (this.attachmentData = resp));
        }
      });
  }

  ngOnDestroy(): void {
    this.attachmentDataSubscription.unsubscribe();
    this.tabClosedSubscription.unsubscribe();
    this.stopWindowPolling();
  }

  private checkAndCloseDialog(): void {
    if (this.childWindow?.closed && this.dialogReference) {
      this.dialogReference.close();
      this.stopWindowPolling();
      window.focus();
    }
  }

  private startWindowPolling(): void {
    this.stopWindowPolling(); // Clear any existing interval

    this.windowCheckInterval = setInterval(() => {
      this.ngZone.run(() => {
        this.checkAndCloseDialog();
      });
    }, 1000); // Check every second
  }

  private stopWindowPolling(): void {
    if (this.windowCheckInterval) {
      clearInterval(this.windowCheckInterval);
      this.windowCheckInterval = null;
    }
  }

  @HostListener('window:focus', ['$event'])
  onWindowFocus(event: FocusEvent) {
    // When main window regains focus, check if child window was closed
    setTimeout(() => {
      this.checkAndCloseDialog();
    }, 100);
  }

  @HostListener('window:unload', ['$event'])
  unloadHandler(event: Event) {
    //('Clean up tab if iframe is closed');
    this.loadermessagingService.closeChildWindow(this.childWindow);
  }

  setPaymentMethodSelection() {
    const selectedMethodId = this.queryParamsService.getQueryParams().method;
    const selectedMethodIndex = this.availablePaymentMethods.findIndex(
      (method) => method.id === selectedMethodId
    );
    this.handlePaymentMethodSelection(
      selectedMethodIndex !== -1 ? selectedMethodIndex : 0
    );
  }

  handlePaymentMethodSelection(index: number) {
    if (
      this.availablePaymentMethods == null ||
      this.availablePaymentMethods.length === 0
    ) {
      return;
    }

    const methodIdentifier = this.availablePaymentMethods[index];
    this.selectedPaymentMethod = this.availablePaymentMethods[index];
    this.setChosenMethodQueryParam(methodIdentifier);
  }

  getPaymentMethodViewContext(method: AuthorizedPaymentMethod) {
    return this.paymentDetail.getPaymentMethodViewContext(
      method,
      this.market,
      this.paymentRequest
    );
  }

  handleCloseClick() {
    this.loadermessagingService.closeChildWindow(this.childWindow);
    this.navigationInProgress = false;
  }

  handleContinueClick() {
    if (this.navigationInProgress) {
      return;
    }
    this.loggingService.warn(
      'selected payment method and navigating: ' +
        this.selectedPaymentMethod.type
    );

    this.navigationInProgress = true;

    this.handleNavigation();
  }

  createButtons() {
    let buttons = [];
    //You can't focus on new tab with mobile.
    if (!this.isMobile) {
      buttons.push({
        title: 'Öppna popup-fönstret',
        callback: () => {
          this.childWindow.focus();
          this.createNewWindowBlocker();
        },
      });
    }
    buttons.push({
      title: 'Välj en annan betalmetod',
      callback: () => {
        this.handleCloseClick();
      },
    });

    return buttons;
  }

  private createNewWindowBlocker() {
    const dialogConfiguration = {
      maxHeight: '50vh',
      maxWidth: '80vw',
      data: {
        title: 'Fortsätt i popup-fönstret',
        reason:
          'Du har öppnat ett popup-fönster. Slutför din åtgärd där. Om du inte hittar det, kan du öppna det på nytt.',
        buttons: this.createButtons(),
      },
    };
    this.dialogReference = this.dialogService.open(
      CreditDecisionDialogComponent,
      dialogConfiguration
    );

    // Start polling to check if child window is closed
    this.startWindowPolling();

    // Also listen for when dialog is manually closed
    this.dialogReference.afterClosed().subscribe(() => {
      this.stopWindowPolling();
      this.dialogReference = null;
    });
  }

  async handleNavigation(): Promise<void> {
    if (this.isBackendDrivenSelection(this.selectedPaymentMethod.type)) {
      const token = this.stateService.getToken();
      if (!token) {
        this.loggingService.error("Can't handle backend-driven nagigation without a token! Backend call will explode.");
      }

      this.handleBackendDrivenNagivation(token);
    } else  {
      this.handleFrontendDrivenNavigation();
    }
  }

  async handleBackendDrivenNagivation(token: string): Promise<void> {
    this.navigationInProgress = true;
    let windowMode: string = this.iframeService.getWindowMode(token);
    let nextRoute: string;

    try {
      nextRoute = await this.stateService
        .registerChosenMethod(this.selectedPaymentMethod.id)
        .then((checkoutState) => {
          return this.stateService.getRouteFromState(checkoutState);
        });
    } catch (error) {
      this.loggingService.warn(
        `Error registering chosen payment method, error: ${error}`
      );
      this.navigate();
    }

    if (this.isIframe && windowMode === WindowMode.POPUP) {
      this.createTabClosedSubscription();

      this.childWindow = this.iframeService.openZaverPopup(
        `/${nextRoute}?id=${token}`
      );

      // Open up blocker/modal saying another window is open...
      this.createNewWindowBlocker();
    } else if (
      (this.isIframe && windowMode === WindowMode.TAB) ||
      (this.isIframe &&
        this.isMobile &&
        !this.iframeService.isThisAChildWindow())
    ) {
      this.createTabClosedSubscription();

      this.childWindow = this.iframeService.openZaverTab(
        `/${nextRoute}?id=${token}`
      );

      // Open up blocker/modal saying another window is open...
      this.createNewWindowBlocker();
    } else {
      this.router.navigate([nextRoute], { queryParamsHandling: 'preserve' });
    }

    this.navigationInProgress = false;
  }

  handleFrontendDrivenNavigation(): void {
    const params = this.queryParamsService.getQueryParams();
    const id = params.id;

    this.customerJourneyService
      .beginJourney(this.selectedPaymentMethod.type)
      .subscribe((route) => {
        if (this.iframeService.getWindowMode(id) === WindowMode.POPUP) {
          this.loggingService.warn(
            'Attempting to open a pop up window to route: ' + route
          );
          this.loadermessagingService.closeChildWindow(this.childWindow);
          this.childWindow = this.iframeService.openZaverPopup(
            route + window.location.search
          );
          this.createNewWindowBlocker();
          this.navigationInProgress = false;
        } else if (
          this.iframeService.getWindowMode(id) === WindowMode.TAB ||
          (this.isIframe && this.isMobile)
        ) {
          this.loggingService.warn(
            'Attempting to open a new tab to route: ' + route
          );
          this.loadermessagingService.closeChildWindow(this.childWindow);
          this.childWindow = this.iframeService.openZaverTab(
            route + window.location.search,
            this.isMobile
          );
          this.createNewWindowBlocker();
          this.navigationInProgress = false;
        } else {
          this.loggingService.warn('Attempting to navigate to route: ' + route);
          this.router
            .navigate([route], { queryParamsHandling: 'merge' })
            .then(() => {
              this.navigationInProgress = false;
            })
            .catch((err) => {
              this.navigationInProgress = false;
              this.loggingService.reportCrash(
                'Navigation from landing page failed.' +
                  'message: ' +
                  err.message,
                null,
                err
              );
            });
        }
      });
  }

  setChosenMethodQueryParam(method: AuthorizedPaymentMethod) {
    // Await end of navigation to assert that the query params
    // will be brought into the UrlTree

    const queryParams = this.queryParamsService.getQueryParams();

    if (
      method.id === queryParams.method &&
      method.type === queryParams.methodType
    ) {
      return;
    }

    const urlTree = this.router.parseUrl(this.router.url);

    for (const key in queryParams) {
      if (queryParams.hasOwnProperty(key)) {
        urlTree.queryParams[key] = queryParams[key];
      }
    }

    urlTree.queryParams['method'] = method.id;
    urlTree.queryParams['methodType'] = method.type;

    // Navigate and unsubscribe

    this.router.navigateByUrl(urlTree);
  }
  // TODO: this should be activateModal

  deactivateModal() {
    this.modalActive = false;
  }

  /**
   * In-house terms are shown at the last step of the instant debit flow.
   */
  shouldShowTermsLink(method: AuthorizedPaymentMethod) {
    return false;
  }

  setModalInfo(information?: 'terms'): void {
    const lineItems = this.paymentRequest.lineItems.length > 0;
    const totalValue = lineItems ? 'Totalbelopp inkl. moms:' : 'Totalbelopp:';

    this.selectorItems = [
      {
        tab: 'Ditt köp',
        title: lineItems ? 'Sammanfattning' : this.paymentRequest.title,
        lineItems: lineItems ? this.paymentRequest.lineItems : null,
        descriptions: [{ text: this.paymentRequest.description }],
        totalValue: `${totalValue} ${this.valuePipe.transform(
          this.paymentRequest.value
        )}`,
      },
    ];

    this.selectedItem = this.selectorItems[0];
    if (information === 'terms') {
      if (this.selectedPaymentMethod.type === PaymentMethod.INSTANT_DEBIT) {
        this.selectorItems = [
          ...this.selectorItems,
          {
            tab: 'Betalningsvillkor',
            title: 'Villkor för betala nu',
            descriptions: [
              {
                title: '',
                text: 'Betalning via Autogiro hanteras av vår samarbetspartner Trustly.',
              },
              {
                title: 'Misslyckad autogirodebitering',
                text: 'Skulle täckning saknas på ditt konto när betalningstransaktionen ska genomföras kommer betalningen istället hanteras av Zaver och du kommer få en faktura från Zaver (Frink AB). Genom att godkänna betalning genom Autogiro godkänner du således att Zaver, vid misslyckad autogirodebitering, ställer ut en faktura till dig. Zaver ansvarar då, istället för Trustly, för att genomföra relevant betaltjänst så du som konsument uppfyller dina åtaganden mot handlaren.',
              },
              {
                title: 'Avgifter och dröjsmålsränta',
                text: 'Fakturan ska betalas inom den tid som anges på fakturan. Vi förbehåller oss rätten att ta ut en administrationsavgift på upp till 49 kr på fakturan. Vid sen betalning av fakturan har vi rätt att ta ut en påminnelseavgift om 60 kr samt debitera dröjsmålsränta på det förfallna beloppet till dess att full betalning sker. Dröjsmålsräntan utgår med 20 % per år. Sker ingen betalning därefter kan fordran komma att överlämnas för inkassohantering där ytterligare inkassokostnader kan tillkomma.',
              },
              {
                title: 'Kreditprövning',
                text: 'Notera att en kreditprövning görs när du ger ditt medgivande till betalning via Autogiro vilket i vissa fall innebär att en kreditupplysning inhämtas från extern part. Du får då en kopia på kreditupplysningen per fysisk och/eller digital post.',
              },
              {
                title: 'Personuppgifter',
                text: 'Zaver behandlar dina personuppgifter för att uppfylla våra åtaganden enligt detta avtal och för de övriga syften som redovisas i vår Integritetspolicy.',
              },
            ],
          },
        ];
      }
      this.selectedItem = this.selectorItems[1];
    }

    if (this.attachmentData.length > 0) {
      const attachmentsArray = this.attachmentData.map(
        (data: AttachmentData) => ({
          id: data.id,
          displayMode: data.displayMode,
          displayName: data.displayName,
          url: data.url,
          authentications: data.authentications,
        })
      );

      this.selectorItems = [
        ...this.selectorItems,
        {
          tab: 'Från säljaren',
          title: `Information från ${this.paymentRequest.merchantDetails.companyName}`,
          attachments: attachmentsArray,
        },
      ];
    }

    this.modalActive = true;
  }
  
  createTabClosedSubscription(): void {
    if (!this.tabClosedSubscription) {
      this.tabClosedSubscription = this.loadermessagingService.event$
        .pipe(filter((message) => message === 'closed')) // Loader Messaging Service sent message 'closed'
        .subscribe(() => {
          this.ngZone.run(() => {
            this.childWindow = null;
          });
        });
    }
  }

  isBackendDrivenSelection(paymentMethod: PaymentMethod): boolean {
    if (!paymentMethod) {
      return false;
    }

    switch (paymentMethod) {
      case PaymentMethod.INSTANT_DEBIT:
      case PaymentMethod.ZAVER_A2A:
        return true;
      default:
        return false;
    }
  }
}
